import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonRow,
  IonText,
  ModalController,
} from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import { IStopAddress } from '@okcargo-apps/shared-domain';
import { IPlace } from '@okcargo-apps/shared-domain-ionic';
import { SearchAddressbookModalComponent, SearchCityPostalcodeModalComponent } from '../../freight-shipper';
import { DecaStopFormModel, IPreviousAddressData, IStopLocationData } from '../deca-forms.model';

@Component({
  selector: 'lib-deca-edit-stop',
  templateUrl: './deca-edit-stop.component.html',
  imports: [
    IonText,
    IonIcon,
    IonButton,
    IonLabel,
    IonItem,
    IonRow,
    IonGrid,
    IonCol,
    ReactiveFormsModule,
    IonInput,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DecaEditStopComponent {
  public form = input.required<FormGroup<DecaStopFormModel>>();
  public locationData = input<IStopLocationData | null>(null);
  public previousAddressData = input<IPreviousAddressData | null>(null);

  public readonly locationChange = output<IStopLocationData | null>();

  private readonly modalCtrl = inject(ModalController);

  public readonly locationSignal = linkedSignal<IStopLocationData | null>(() => this.locationData());

  public readonly isFormDisabled = computed(() => this.form().disabled);

  public readonly dateHelperText = computed(() => {
    const formGroup = this.form();
    const dateControl = formGroup.controls.date;

    if (formGroup.disabled) {
      return 'Día y hora prevista';
    }

    if (dateControl.disabled) {
      return 'La fecha de origen ya no puede modificarse';
    }

    return 'Día y hora prevista';
  });

  public getErrorMessage(controlName: keyof DecaStopFormModel): string | null {
    const control = this.form().controls[controlName];
    if (!control || !control.errors) {
      return null;
    }

    const firstErrorKey = Object.keys(control.errors)[0];
    const errorData = control.errors[firstErrorKey];

    if (typeof errorData === 'string') {
      return errorData;
    }

    return errorData?.message || null;
  }

  public async openAddressbookModal(): Promise<void> {
    const modal = await this.modalCtrl.create({ component: SearchAddressbookModalComponent });
    await modal.present();
    const { data, role } = await modal.onWillDismiss<IStopAddress>();

    if (role === 'confirm' && data) {
      this.form().patchValue({
        companyName: data.company,
        street: data.street,
      });
      this.form().updateValueAndValidity();

      const location: IStopLocationData = {
        city: data.city,
        postalCode: data.postalCode,
        stateCode: data.stateCode,
        countryCode: data.countryCode,
      };

      this.locationSignal.set(location);
      this.locationChange.emit(location);
    }
  }

  public async openPostalCodeModal(): Promise<void> {
    const modal = await this.modalCtrl.create({ component: SearchCityPostalcodeModalComponent });
    await modal.present();
    const { data, role } = await modal.onWillDismiss<IPlace>();

    if (role === 'confirm' && data) {
      this.form().updateValueAndValidity();

      const location: IStopLocationData = {
        city: data.city,
        postalCode: data.postalCode,
        stateCode: data.stateCode,
        countryCode: data.countryCode,
      };

      this.locationSignal.set(location);
      this.locationChange.emit(location);
    }
  }

  public copyPreviousAddress(): void {
    const prev = this.previousAddressData();
    if (!prev) return;

    const isComplete = !!prev.companyName?.trim() && !!prev.street?.trim() && !!prev.location?.postalCode?.trim();
    if (!isComplete) return;

    this.form().patchValue({
      companyName: prev.companyName,
      street: prev.street,
    });
    this.form().updateValueAndValidity();

    this.locationSignal.set(prev.location);
    this.locationChange.emit(prev.location);
  }
}
